/**
 * The library's DEFAULT, in the drawer: with nothing asked of it, the catalogue offers the day's
 * own muscle groups rather than all 479 movements.
 *
 * ── This is the mock's rule, and it is concrete ──────────────────────────────────────────────
 * `Builder planu - tydzien - programator.dc.html` filters its pool with
 *
 *   `if (!nSel) return dayMg.indexOf(x[1]) !== -1;`
 *
 * where `dayMg` is `SEL.load.map((m) => m[0])` — the display groups the open day's own load strip
 * is drawn from — and `x[1]` is the movement's group. It captions the result "Pasują do dnia
 * <nazwa dnia>". An earlier round declined to draw that caption on the reading that nothing
 * answers "does this movement match this day"; the mock answers it itself, out of data that is
 * already on the screen, so the rule ships.
 *
 * The day's groups come through `muscleLoadByDay`, which is the same computation the panel's load
 * strip renders — so the drawer's default and the strip above it can never disagree about what the
 * day trains. Its `entries` are the NON-ZERO groups only, which is exactly the list wanted here.
 *
 * ── A movement's group is its PRIMARY one ────────────────────────────────────────────────────
 * The mock gives each pool row a single coarse group and matches on it. Ours carries fourteen
 * backend groups over two tiers, so the closest true reading is the primaries rolled up to the six
 * display buckets: those are what a trainer means by "this is a chest movement". Including
 * SECONDARIES would put every compound press in every day, which is the default failing to be one.
 *
 * ── The two escapes, and why a default needs one ─────────────────────────────────────────────
 * A default that cannot be got out of is a trap rather than a default, so this one has two exits
 * and both are visible. Narrowing the catalogue by hand — any facet, or a search — suppresses it,
 * which is the mock's own rule (`nSel`) plus search, because hiding a movement a trainer has just
 * typed the name of is a search that looks broken. And the caption itself is the second: it is a
 * BUTTON in the drawer, and pressing it lifts the day default for the rest of the reach, which is
 * the one press that reaches the whole library. `isLifted` is that press.
 *
 * ── A day with no load is not narrowed at all ────────────────────────────────────────────────
 * A day the trainer has just created trains nothing, so its group list is empty — and matching
 * against an empty list would offer an empty library over a caption claiming it fits the day. The
 * default simply does not apply there, the caption is not printed, and the whole catalogue is
 * offered, which is what a trainer opening the library on a blank day is asking for.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { ExerciseListItem } from '~/entities/exercise-library';
import {
  displayGroupOf,
  muscleLoadByDay,
  type CountedExercise,
  type MuscleAttributionMap,
  type MuscleDisplayGroup,
} from '~/entities/plan-draft';

export interface DayAffinityInput {
  /** What the query already answered — this narrows it further, never widens it. */
  readonly results: readonly ExerciseListItem[];
  /** The open day's counting exercises. Empty for a day that prescribes nothing yet. */
  readonly counted: readonly CountedExercise[];
  /** Exercise id → what it trains, from the same catalogue read the results came out of. */
  readonly attribution: MuscleAttributionMap;
  /** How many facet selections are active. The mock's own `nSel`. */
  readonly activeFilterCount: number;
  /**
   * The search field's raw contents.
   *
   * **The mock's rule is `nSel` alone, and taking the search in with it is a stated departure.**
   * Its search field is a static label with nothing behind it, so the question never arose there.
   * Here it is real, and a trainer who types the name of a movement the day does not train would
   * otherwise be shown an empty library — a search that looks broken, over a caption explaining
   * something else. A term typed by hand is a narrowing the trainer expressed; the default exists
   * for the reach where they expressed none.
   */
  readonly searchText: string;
  /** True once the trainer has pressed the caption to reach the whole library. */
  readonly isLifted: boolean;
}

export interface DayAffinityView {
  readonly results: readonly ExerciseListItem[];
  /**
   * True when the results above ARE the day's subset. The caption prints on exactly this, so the
   * sentence and the list it describes cannot come apart.
   */
  readonly isApplied: boolean;
  /** The day's own display groups, in the load strip's order. Empty when the day trains nothing. */
  readonly dayGroups: readonly MuscleDisplayGroup[];
}

/** The display buckets one movement's PRIMARY muscles roll up to, de-duplicated. */
function primaryGroupsOf(item: ExerciseListItem): MuscleDisplayGroup[] {
  const groups = new Set<MuscleDisplayGroup>();
  for (const muscle of item.primaryMuscles) {
    // `displayGroupOf` speaks the WIRE's names; `primaryMuscles` are this app's slugs. The two are
    // the same words in different casing, and `displayGroupOf` lower-cases what it is given — which
    // is why `fullBody` is the one that would break a naive pass, and it does not: its table key is
    // `fullbody`, and the slug lower-cases to exactly that.
    const group = displayGroupOf(muscle);
    if (group !== null) groups.add(group);
  }
  return [...groups];
}

/** The library's default, applied — or declined, with the reason visible in `isApplied`. */
export function dayAffinity(input: DayAffinityInput): DayAffinityView {
  const dayGroups =
    input.counted.length === 0
      ? []
      : (muscleLoadByDay([input.counted], input.attribution).days[0]?.entries ?? []).map(
          (entry) => entry.group,
        );

  const isNarrowed = input.activeFilterCount > 0 || input.searchText.trim().length > 0;
  const isApplied = !isNarrowed && !input.isLifted && dayGroups.length > 0;
  if (!isApplied) return { results: input.results, isApplied: false, dayGroups };

  return {
    results: input.results.filter((item) =>
      primaryGroupsOf(item).some((group) => dayGroups.includes(group)),
    ),
    isApplied: true,
    dayGroups,
  };
}
